<template>

  <section class="jackpot-box" :aria-label="$t('jackpot.title')">

    <div class="jackpot-box__frame">

      <div class="jackpot-box__inner">

        <div class="jackpot-box__aura" aria-hidden="true" />

        <div class="jackpot-box__sheen" aria-hidden="true" />

        <header class="jackpot-box__head">

          <div class="jackpot-box__head-copy">

            <span class="jackpot-box__badge">MEGA</span>

            <h3 class="jackpot-box__title">{{ $t('jackpot.title') }}</h3>

          </div>

        </header>

        <div

          class="jackpot-box__meter"

          :class="{ 'jackpot-box__meter--active': isAnimating }"

        >

          <div class="jackpot-box__meter-glow" aria-hidden="true" />

          <div class="jackpot-box__meter-rim" aria-hidden="true" />

          <p class="jackpot-box__amount" aria-live="polite">

            <span class="jackpot-box__currency">{{ siteCurrency }}</span>

            <span class="jackpot-box__value">{{ formattedAmount }}</span>

          </p>

        </div>

        <footer class="jackpot-box__foot">

          <span class="jackpot-box__pulse" aria-hidden="true" />

          <span class="jackpot-box__foot-text">{{ $t('jackpot.live') }}</span>

        </footer>

      </div>

    </div>

  </section>

</template>

<script>

import { resolveSiteCurrency } from '@/utils/siteBrand'
import {
  formatJackpotDisplayValue,
  isDollarJackpotCurrency,
  normalizeJackpotValue,
  parseRuntimeJackpot
} from '@/utils/jackpot'
import { fetchRuntimeJackpot, RUNTIME_JACKPOT_POLL_MS } from '@/utils/runtimeJackpot'

const ANIM_MIN_MS = 720
const ANIM_MAX_MS = 1200

export default {

  name: 'BlackGoldJackpotBox',

  data () {
    return {
      displayValue: null,
      hasInitialRuntimeJackpot: false,
      isAnimating: false,
      rafId: null,
      runtimePollTimer: null
    }
  },

  computed: {

    runtimeJackpot () {
      return this.$store.state.runtimeJackpot
    },

    jackpotCurrency () {
      return resolveSiteCurrency(this.$store.state.siteInfo)
    },

    isDollarCurrency () {
      return isDollarJackpotCurrency(this.jackpotCurrency)
    },

    formattedAmount () {
      return formatJackpotDisplayValue(this.displayValue, this.jackpotCurrency)
    }

  },

  watch: {

    runtimeJackpot: {

      immediate: true,

      handler (value) {
        this.applyRuntimeJackpot(value)
      }

    }

  },

  mounted () {
    this.syncRuntimeJackpot()
    this.runtimePollTimer = window.setInterval(() => {
      this.syncRuntimeJackpot()
    }, RUNTIME_JACKPOT_POLL_MS)
  },

  beforeDestroy () {
    this.clearJackpotTimers()
    this.stopRuntimePoll()
  },

  methods: {
    async syncRuntimeJackpot () {
      await fetchRuntimeJackpot(this.$store.state.siteInfo, {
        skipSessionGuard: true
      })
    },

    stopRuntimePoll () {
      if (this.runtimePollTimer) {
        window.clearInterval(this.runtimePollTimer)
        this.runtimePollTimer = null
      }
    },

    applyRuntimeJackpot (value) {
      const target = normalizeJackpotValue(value, this.jackpotCurrency)
      if (target == null) return

      const current = normalizeJackpotValue(this.displayValue, this.jackpotCurrency)
      if (this.hasInitialRuntimeJackpot && current != null && current === target) return

      if (!this.hasInitialRuntimeJackpot) {
        this.clearJackpotTimers()
        this.displayValue = target
        this.hasInitialRuntimeJackpot = true
        this.isAnimating = false
        return
      }

      this.animateTo(target)
    },

    animateTo (target) {
      if (this.rafId) {
        window.cancelAnimationFrame(this.rafId)

        this.rafId = null
      }

      const parsedTarget = parseRuntimeJackpot(target)

      if (parsedTarget == null) return

      const end = normalizeJackpotValue(parsedTarget, this.jackpotCurrency)

      if (end == null) return

      const start = Number(this.displayValue)

      const from = Number.isFinite(start) ? start : end

      const startTime = performance.now()

      const duration = ANIM_MIN_MS + Math.floor(Math.random() * (ANIM_MAX_MS - ANIM_MIN_MS))

      this.isAnimating = true

      const step = (now) => {
        const progress = Math.min(1, (now - startTime) / duration)

        const eased = 1 - Math.pow(1 - progress, 3)

        let next = from + (end - from) * eased

        if (this.isDollarCurrency) {
          next = Math.round(next * 100) / 100
        } else {
          next = Math.floor(next)
        }

        if (next !== this.displayValue) {
          this.displayValue = next
        }

        if (progress < 1) {
          this.rafId = window.requestAnimationFrame(step)

          return
        }

        this.displayValue = end

        this.isAnimating = false

        this.rafId = null
      }

      this.rafId = window.requestAnimationFrame(step)
    },

    clearJackpotTimers () {
      if (this.rafId) {
        window.cancelAnimationFrame(this.rafId)

        this.rafId = null
      }
    }

  }

}

</script>
